import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { toast } from "sonner";

import type { BillRow } from "@shared/schemas/db";
import type { Invoice } from "@shared/schemas/entities";
import type {
  NotificationType,
  NotificationsResponse,
} from "@shared/schemas/notifications";
import { sourceEntityIdFromItemId } from "@shared/notifications/itemId";

import { apiGet, apiPost, apiPut } from "@/lib/api/client";
import { queryKeys } from "@/lib/query/keys";

// ---------------------------------------------------------------------------
// useNotifications — Session 18j (Baustein A4), erweitert 5.24
// ---------------------------------------------------------------------------
// Ein Hook fuer das Notification Center. Mutation-Hooks gehoeren in dieselbe
// Datei wie der Query-Hook — Invalidierung + Endpoint-Pfade haben einen
// gemeinsamen Fixpunkt.
//
// Phase 5.24: Die binaeren Accept/Reject-Hooks fuer `invoice_match_pending`
// sind weg. Stattdessen `useAssignInvoiceToProject`, das das neue Modal
// bedient (Projekt-Wahl ODER "Keinem Projekt zuweisen"). Der gleiche Hook
// ersetzt auch die alten Confirm-/Reject-Suggestion-Hooks auf der Kunden-
// Detail-Seite — eine Quelle, ein Endpoint, ein UI-Pattern.

// Phase 5.72-C — der Endpoint liefert nur noch generatedAt + items
// (materialisierter SELECT, kein Cache-Fallback).
type NotificationListResult = NotificationsResponse["data"];

export function useNotifications() {
  return useQuery<NotificationListResult>({
    queryKey: queryKeys.notifications.list(),
    queryFn: async () => {
      const res = await apiGet<NotificationsResponse>("/api/notifications");
      return res.data;
    },
    staleTime: 60_000,
    // Phase 5.48 — alte Inbox sichtbar lassen waehrend Refetch laeuft.
    // Verhindert White-Screen auf der Home, wenn /api/notifications einen
    // Vercel-60s-Timeout (FUNCTION_INVOCATION_TIMEOUT) trifft und der naechste
    // Tab-Switch sofort einen neuen Versuch ausloest.
    placeholderData: keepPreviousData,
  });
}

function invalidateAll(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: queryKeys.notifications.all() });
}

function invalidateAfterInvoiceDecision(
  qc: ReturnType<typeof useQueryClient>,
  invoice: Invoice | null,
) {
  invalidateAll(qc);
  void qc.invalidateQueries({ queryKey: queryKeys.invoices.all() });
  void qc.invalidateQueries({ queryKey: queryKeys.projects.all() });
  if (invoice?.customerId) {
    void qc.invalidateQueries({
      queryKey: queryKeys.invoices.suggestionsByCustomer(invoice.customerId),
    });
    void qc.invalidateQueries({
      queryKey: queryKeys.invoices.listByCustomer(invoice.customerId),
    });
  }
  if (invoice?.projectId) {
    void qc.invalidateQueries({
      queryKey: queryKeys.invoices.listByProject(invoice.projectId),
    });
    void qc.invalidateQueries({
      queryKey: queryKeys.projects.events(invoice.projectId),
    });
  }
}

function invalidateAfterOwnerDecision(qc: ReturnType<typeof useQueryClient>) {
  // Accept setzt `projects.ownerUserId` — die ranked-scope=mine-Listen werden
  // sich veraendern. Breiter projects.all()-Sweep wie in 18i-A.
  invalidateAll(qc);
  void qc.invalidateQueries({ queryKey: queryKeys.projects.all() });
}

interface AssignResponse {
  ok: true;
  data: Invoice;
}

interface GenericOk {
  ok: true;
}

// invoice_match_pending — manueller Assign aus dem Zuweisungs-Modal (5.24).
// projectId === null bedeutet "Keinem Projekt zuweisen" (Material-Rechnung).
export function useAssignInvoiceToProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      invoiceId: string;
      projectId: string | null;
    }) => {
      const res = await apiPost<AssignResponse>(
        `/api/invoices/suggestions?action=assign&id=${encodeURIComponent(input.invoiceId)}`,
        { projectId: input.projectId },
      );
      return res.data;
    },
    onSuccess: (invoice) => invalidateAfterInvoiceDecision(qc, invoice),
    onError: () => invalidateAfterInvoiceDecision(qc, null),
  });
}

// bill_match_pending — manueller Assign Phase 5.25 K2.
interface BillAssignResponse {
  ok: true;
  data: BillRow;
}

function invalidateAfterBillDecision(
  qc: ReturnType<typeof useQueryClient>,
  bill: BillRow | null,
) {
  invalidateAll(qc);
  void qc.invalidateQueries({ queryKey: queryKeys.bills.all() });
  // FB-M4a: der Allokations-Key haengt NICHT unter ["bill"] — ohne diese
  // Zeile blieben Nachkalk-Tab und Kosten-Abschnitt nach einer Zuordnung
  // auf dem alten Stand.
  void qc.invalidateQueries({ queryKey: ["bill-allocations"] });
  void qc.invalidateQueries({ queryKey: queryKeys.projects.all() });
  if (bill?.projectId) {
    void qc.invalidateQueries({
      queryKey: queryKeys.bills.listByProject(bill.projectId),
    });
    void qc.invalidateQueries({
      queryKey: queryKeys.projects.events(bill.projectId),
    });
  }
}

export interface BillAllocationInput {
  projectId: string;
  amount: string;
  notes?: string | null;
  // 5.27 K1: optional. NULL bzw. weggelassen = "aufs Projekt insgesamt".
  calcItemId?: string | null;
}

export function useAssignBillAllocations() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      billId: string;
      allocations: ReadonlyArray<BillAllocationInput>;
    }) => {
      const res = await apiPost<BillAssignResponse>(
        `/api/bills/suggestions?action=assign&id=${encodeURIComponent(input.billId)}`,
        { allocations: input.allocations },
      );
      return res.data;
    },
    onSuccess: (bill) => invalidateAfterBillDecision(qc, bill),
    onError: () => invalidateAfterBillDecision(qc, null),
  });
}

// project_owner_suggestion — Primaer-Aktion „Zuweisen".
export function useAcceptOwnerSuggestion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (suggestionId: string) => {
      const res = await apiPost<GenericOk>(
        `/api/projects/owner-suggestions?action=accept&id=${encodeURIComponent(suggestionId)}`,
        {},
      );
      return res;
    },
    ...optimisticNotificationRemoval<string>(
      qc,
      (id) => (item) =>
        item.type === "project_owner_suggestion" &&
        item.suggestionId === id,
    ),
    onSuccess: () => invalidateAfterOwnerDecision(qc),
  });
}

// MAIL-2 — ebau_notice_candidate «Ja, meins» / ebau_notice_unassigned
// «Zuordnen»: die Dossier-Nummer ans gewaehlte Projekt (Quelle `karte`). Der
// Server verarbeitet die liegende Meldung damit; die Karten zu diesem Dossier
// verschwinden bei allen, hier sofort optimistisch.
export function useAssignEbauDossier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { projectId: string; dossierNr: string }) => {
      const res = await apiPost<GenericOk>(
        `/api/projects/ebau-dossier?projectId=${encodeURIComponent(input.projectId)}`,
        { dossierNr: input.dossierNr, source: "karte" },
      );
      return res;
    },
    ...optimisticNotificationRemoval<{ projectId: string; dossierNr: string }>(
      qc,
      (input) => (item) =>
        (item.type === "ebau_notice_candidate" ||
          item.type === "ebau_notice_unassigned") &&
        item.dossierNr === input.dossierNr,
    ),
    onSuccess: (_data, input) => {
      toast.success(`Dossier ${input.dossierNr} ist dem Projekt zugeordnet.`);
      void qc.invalidateQueries({
        queryKey: queryKeys.projects.detail(input.projectId),
      });
      void qc.invalidateQueries({
        queryKey: queryKeys.projects.steps(input.projectId),
      });
      void qc.invalidateQueries({
        queryKey: queryKeys.projects.events(input.projectId),
      });
    },
  });
}

// project_owner_suggestion — Sekundaer-Aktion „Verwerfen".
export function useRejectOwnerSuggestion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (suggestionId: string) => {
      const res = await apiPost<GenericOk>(
        `/api/projects/owner-suggestions?action=reject&id=${encodeURIComponent(suggestionId)}`,
        {},
      );
      return res;
    },
    ...optimisticNotificationRemoval<string>(
      qc,
      (id) => (item) =>
        item.type === "project_owner_suggestion" &&
        item.suggestionId === id,
    ),
    onSuccess: () => invalidateAfterOwnerDecision(qc),
  });
}

// HOME-13 (2026-07-30): `useAcceptFolderMove` / `useDismissFolderMove` standen
// hier. Der Ordner-Drift ist keine Meldung mehr — die Aktionen leben jetzt in
// `hooks/useFolderDrift.ts`, gelesen und entschieden am Projekt.

// folder_link_suggestion — OD-2c „Zuordnen": Ordner-Waise dem
// vorgeschlagenen Lead verknuepfen (Folder-Refs + ggf. Code aufs Projekt).
export function useLinkFolderSuggestion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (suggestionId: string) => {
      const res = await apiPost<GenericOk>(
        "/api/projects/folder-link-suggestions?action=link",
        { id: suggestionId },
      );
      return res;
    },
    ...optimisticNotificationRemoval<string>(
      qc,
      (id) => (item) =>
        item.type === "folder_link_suggestion" && item.suggestionId === id,
    ),
    onSuccess: () => invalidateAll(qc),
  });
}

// folder_link_suggestion — OD-2c „Neues Projekt": Vorschlag ablehnen +
// Ordner sofort als eigenes Projekt importieren (Ingest-Pfad).
export function useImportFolderSuggestion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (suggestionId: string) => {
      const res = await apiPost<GenericOk>(
        "/api/projects/folder-link-suggestions?action=import",
        { id: suggestionId },
      );
      return res;
    },
    ...optimisticNotificationRemoval<string>(
      qc,
      (id) => (item) =>
        item.type === "folder_link_suggestion" && item.suggestionId === id,
    ),
    onSuccess: () => invalidateAll(qc),
  });
}

// OD-3a — «Als Dublette schliessen»: bestehender leadClose-Pfad mit Grund
// duplicate + Verweis aufs fuehrende Projekt in der note (kein Delete).
// HOME-14 (2026-07-31): `useCloseLeadDuplicate` lebt jetzt in
// `hooks/useLeadDuplicates.ts` — dort, wo die Dublette angezeigt wird. Der
// Endpoint ist derselbe; nur die Karte, die er wegraeumte, gibt es nicht mehr.

// OD-3 — Fingerprint-Ablehnung (Kunden-Vorschlag ablehnen / «Behalten» bei der
// Leere-Projekte-Frage). EIN Endpoint, kind steuert. «Keine Dublette» laeuft
// seit HOME-14 ueber `useDismissLeadDuplicate` am Projekt.
export function useDismissSuggestionFingerprint() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      projectId: string;
      kind: "customer_link" | "empty_keep";
      fingerprint: string;
    }) => {
      const res = await apiPost<GenericOk>(
        `/api/projects/status-suggestion-dismiss?id=${encodeURIComponent(input.projectId)}`,
        { kind: input.kind, fingerprint: input.fingerprint },
      );
      return res;
    },
    ...optimisticNotificationRemoval<{
      projectId: string;
      kind: "customer_link" | "empty_keep";
      fingerprint: string;
    }>(
      qc,
      (input) => (item) =>
        item.type === FINGERPRINT_KIND_TO_NOTIFICATION_TYPE[input.kind] &&
        "projectId" in item &&
        item.projectId === input.projectId,
    ),
    onSuccess: () => invalidateAll(qc),
  });
}

// OD-3d — «Verknuepfen»: Kunde ueber den bestehenden Projekt-Update-Pfad
// setzen (PUT /api/projects — derselbe Weg wie das Bearbeiten-Formular).
export function useLinkSuggestedCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { projectId: string; customerId: string }) => {
      const res = await apiPut<GenericOk>(
        `/api/projects?id=${encodeURIComponent(input.projectId)}`,
        { customerId: input.customerId },
      );
      return res;
    },
    ...optimisticNotificationRemoval<{ projectId: string; customerId: string }>(
      qc,
      (input) => (item) =>
        item.type === "project_customer_suggestion" &&
        item.projectId === input.projectId,
    ),
    onSuccess: () => invalidateAll(qc),
  });
}

// OD-3e — «Loeschen» bei der Leere-Projekte-Frage: bestehender Soft-Delete.
export function useDeleteEmptyProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { projectId: string }) => {
      const res = await apiPost<GenericOk>(
        `/api/projects/delete?projectId=${encodeURIComponent(input.projectId)}`,
        { reason: "Leere-Projekte-Frage (OD-3e): löschen bestätigt" },
      );
      return res;
    },
    ...optimisticNotificationRemoval<{ projectId: string }>(
      qc,
      (input) => (item) =>
        item.type === "project_empty_question" &&
        item.projectId === input.projectId,
    ),
    onSuccess: () => invalidateAll(qc),
  });
}

// OD-3b — «Wiederherstellen»: Undo des Soft-Delete (Status zurueck auf den
// Vorher-Status, project_restored-Event). KARTEN-1: die Karte dazu ist
// entfernt; der Knopf lebt in der Projektliste (Ablage «Geloescht»).
export function useRestoreProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { projectId: string }) => {
      const res = await apiPost<GenericOk>(
        `/api/projects/restore?projectId=${encodeURIComponent(input.projectId)}`,
        {},
      );
      return res;
    },
    onSuccess: () => {
      invalidateAll(qc);
      // DATEN-4b: auch aus der Projektliste (Ablage «Geloescht») — die Liste
      // muss das zurueckgeholte Projekt sofort wieder zeigen.
      void qc.invalidateQueries({ queryKey: queryKeys.projects.all() });
    },
  });
}

// OD-5 — «Rueckgaengig» an der Auto-Zuordnungs-Quittung: die automatische
// Bill->Projekt-Zuordnung wird zurueckgenommen, die Zuordnungs-Frage kommt
// zurueck, der Automat fasst die Rechnung nie wieder an.
export function useUndoBillAutoAssign() {
  const qc = useQueryClient();
  return useMutation<GenericOk, Error, { billId: string }, DismissContext>({
    mutationFn: async (input) => {
      const res = await apiPost<GenericOk>(
        `/api/bills/suggestions?action=undo-auto&id=${encodeURIComponent(input.billId)}`,
        {},
      );
      return res;
    },
    // FB-Sandro 2: dieser Knopf hing als EINZIGER der Karten-Aktionen am
    // reinen Neuabruf — sein direkter Nachbar `useUndoOwnerAutoAssign` raeumt
    // die Karte laengst sofort weg. Eine Karte, die anders reagiert als die
    // daneben, ist schlimmer als eine, die gar nicht reagiert: man haelt es
    // fuer den eigenen Fehlklick und klickt nochmal.
    ...optimisticNotificationRemoval<{ billId: string }>(
      qc,
      (input) => (item) =>
        item.type === "bill_match_auto_assigned" &&
        item.billId === input.billId,
    ),
    onSettled: () => invalidateAll(qc),
  });
}

// OD-5 — «Rueckgaengig» an der PL-Auto-Zuweisungs-Quittung.
export function useUndoOwnerAutoAssign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { suggestionId: string }) => {
      const res = await apiPost<GenericOk>(
        `/api/projects/owner-suggestions?action=undo-auto&id=${encodeURIComponent(input.suggestionId)}`,
        {},
      );
      return res;
    },
    ...optimisticNotificationRemoval<{ suggestionId: string }>(
      qc,
      (input) => (item) =>
        item.type === "project_owner_auto_assigned" &&
        item.suggestionId === input.suggestionId,
    ),
    onSuccess: () => invalidateAll(qc),
  });
}

// LEAD-9 — «Rückgängig» am Angebots-Automaten: das PDF im Ordner war ein
// Entwurf, kein versendetes Angebot. Der Status faellt auf die Stufe zurueck,
// von der der Automat ihn geholt hat; danach fasst er dieses Projekt nie
// wieder an. Gleiche Bauart wie `useUndoOwnerAutoAssign` — der Schluessel ist
// hier die projectId, weil die Quittung am Projekt haengt (nicht an einer
// Vorschlags-Zeile). KARTEN-1: die Karte, die den Knopf trug, ist entfernt.
export function useUndoLeadFolderAdvance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { projectId: string }) => {
      const res = await apiPost<GenericOk>(
        `/api/projects/lead-folder-advance?action=undo&projectId=${encodeURIComponent(input.projectId)}`,
        {},
      );
      return res;
    },
    onSuccess: () => invalidateAll(qc),
  });
}

// 5.77 — Drei-Modi-Dismiss/Snooze. Ein einziger POST-Endpoint deckt:
//   * Item-Dismiss   : { type, sourceEntityId, snoozeDays }
//   * Bundle-Dismiss : { type, bundle: true, snoozeDays }
// snoozeDays = null   -> permanent (M1 Event-Dismiss)
// snoozeDays = N      -> Snooze (M2 Status-Snoozable)
//
// 5.115 — Cache-Invalidate alleine reicht nicht: das Endpoint
// `/api/notifications` ist nicht garantiert <100ms und `useNotifications`
// laeuft mit `placeholderData: keepPreviousData`, also bleibt der alte
// Stand sichtbar bis der Refetch zurueck ist. Sandro klickt „Bis in 7
// Tagen" und sieht 1-3 s lang nichts. Fix: optimistic Cache-Update
// (Bundle/Item sofort aus dem Cache filtern) plus Sonner-Toast.

const TYPE_LABEL_PLURAL_PROSE: Record<NotificationType, string> = {
  invoice_overdue: "Mahnungen",
  invoice_missing_in_bexio: "Hinweise «nicht mehr in bexio»",
  bexio_abgleich_abweichung: "Abweichungen zu bexio",
  invoice_payment_received: "Zahlungseingänge",
  invoice_not_opened: "nie geöffnete Rechnungen",
  invoice_match_pending: "Rechnungs-Zuordnungen",
  bill_match_pending: "Lieferanten-Belege",
  bill_match_auto_assigned: "Auto-zugeordnete Belege",
  bill_allocation_item_orphaned: "Allokations-Prüfungen",
  project_owner_suggestion: "PL-Vorschläge",
  project_owner_auto_assigned: "Projektleiter gesetzt",
  project_ready_to_complete: "Projekt-Abschlüsse",
  project_completion_requested: "Abschluss-Freigaben",
  project_completion_rejected: "zurückgewiesene Abschlüsse",
  waechter_handgriff: "Handgriffe aus Prüfungen",
  project_new_from_website: "Neue Anfragen",
  lead_assigned: "Leads zum Kontaktieren",
  lead_decide_pending: "Offene Richtpreise",
  project_lead_pl_stale: "Stagnierende Leads",
  project_lead_critical: "Kritische Leads",
  portal_order_received: "Bestellungen",
  kpi_threshold_breached: "KPI-Schwellen",
  vat_period_due: "MWST-Fälligkeiten",
  migration_drift_detected: "Migrations-Driftungen",
  step_assignees_orphans_detected: "Step-Driftungen",
  auth_config_drift_detected: "Auth-Config-Driftungen",
  it_alarm_check_failed: "IT-Alarme",
  project_calc_data_missing: "Calc-Fehlen-Hinweise",
  mail_send_blocked: "Blockierte Mails",
  partner_signup_pending: "Neue Partner-Anmeldungen",
  partner_quote_submitted: "Partner-Offerten",
  partner_quote_signed: "Unterschriebene Offerten",
  partner_quote_outcome: "Partner-Abschlüsse",
  order_awaiting_pl_confirm: "Aufträge zur Bestätigung",
  feedback_submitted: "Feedback-Meldungen",
  project_status_suggestion: "Status-Vorschläge",
  pingen_letter_undeliverable: "Unzustellbare Briefe",
  offer_first_portal_visit: "Portal-Erstbesuche",
  offer_follow_up_nudge: "Nachfass-Hinweise",
  offer_portal_link_expiring: "Ablaufende Portal-Links",
  background_push_failed: "Fehlgeschlagene bexio-Syncs",
  liquidity_reserve_breach: "Reserve-Alarme",
  liquidity_monday_briefing: "Montags-Briefings",
  ebau_receipt_pending: "eBau-Quittungen",
  ebau_notice_unassigned: "eBau ohne Projekt",
  ebau_notice_candidate: "eBau-Fragen",
  ebau_decision_received: "Bauentscheide",
  roof_insight_draft: "Richtpreise",
  roof_source_conflict: "Quellen-Widersprüche",
  folder_link_suggestion: "Ordner-Zuordnungen",
  project_customer_suggestion: "Kunden-Zuordnungen",
  project_empty_question: "Leere-Projekte-Fragen",
  portal_message_received: "Kunden-Nachrichten",
  lead_intake_no_contact: "Leads ohne bekannten Kontakt",
  portal_reply_received: "Kunden-Antworten",
  lead_pl_needed: "Projektleiter setzen",
  roof_auto_sent_followup: "Automatisch versendete Richtpreise",
  inbound_mail_skipped: "Übersprungene Mails",
  inbound_mail_in_junk: "Mails im Junk-Ordner",
};

function snoozeToastMessage(args: {
  type: NotificationType;
  snoozeDays: number | null | undefined;
  bundle: boolean;
}): string {
  const label = TYPE_LABEL_PLURAL_PROSE[args.type] ?? "Benachrichtigungen";
  const subject = args.bundle ? label : "Benachrichtigung";
  if (args.snoozeDays === null || args.snoozeDays === undefined) {
    return `${subject} permanent ausgeblendet.`;
  }
  const until = new Date(Date.now() + args.snoozeDays * 24 * 60 * 60 * 1000);
  const formatted = until.toLocaleDateString("de-CH", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
  return `${subject} bis ${formatted} ausgeblendet.`;
}

export interface DismissContext {
  previous: NotificationsResponse["data"] | undefined;
}

/**
 * FB-D: die betroffene Karte SOFORT aus dem Cache nehmen, bei Server-Fehler
 * zurueckrollen. Exportiert, damit karten-eigene Hooks (RO-5
 * `useRoofDocumentRelease`) denselben Weg nehmen statt die Cache-Form ein
 * zweites Mal zu kennen — der Cache haelt `NotificationsResponse["data"]`,
 * nicht die ganze Antwort, und genau daran verschreibt man sich.
 *
 * FB-Sandro 2 (2026-08-20): das `cancelQueries` davor ist der eigentliche
 * Riegel. Sandro klickte drei Mahnungen weg, alle drei standen sauber in der
 * Datenbank — auf dem Schirm wirkte EINE. Nicht weil die Karte nicht ver-
 * schwand, sondern weil sie zurueckkam:
 *
 *   t=0.0s  Klick 1 -> Karte A raus, POST A
 *   t=0.3s  POST A zurueck -> `onSettled` -> invalidate -> Neuabruf R1 laeuft
 *   t=6.0s  Klick 2 -> Karte B raus
 *   t=6.5s  R1 landet — mit dem Server-Stand von t=0.3s, also MIT B drin
 *           -> ueberschreibt den Cache -> B steht wieder da
 *
 * `/api/notifications` ist langsam genug, dass dieses Fenster in Sekunden
 * zaehlt (siehe den `keepPreviousData`-Kommentar oben: Vercel-60s-Timeouts).
 * Jeder Wegklick raeumt darum zuerst die laufenden Abrufe ab — eine Antwort,
 * die aelter ist als die Entscheidung, darf nicht mehr landen.
 */
export async function snapshotAndPatchNotificationCache(
  qc: ReturnType<typeof useQueryClient>,
  predicate: (
    item: NotificationsResponse["data"]["items"][number],
  ) => boolean,
): Promise<DismissContext> {
  const key = queryKeys.notifications.list();
  await qc.cancelQueries({ queryKey: queryKeys.notifications.all() });
  const previous = qc.getQueryData<NotificationsResponse["data"]>(key);
  if (previous) {
    qc.setQueryData<NotificationsResponse["data"]>(key, {
      ...previous,
      items: previous.items.filter((item) => !predicate(item)),
    });
  }
  return { previous };
}

export function restoreNotificationCache(
  qc: ReturnType<typeof useQueryClient>,
  ctx: DismissContext | undefined,
): void {
  if (!ctx || !ctx.previous) return;
  qc.setQueryData(queryKeys.notifications.list(), ctx.previous);
}

// FB-D (Marvin 2026-07-23) — Karten-Aktionen der Startseite geben SOFORT
// Rueckmeldung. Marvin klickte «Dublette»/«Ablehnen» sechsmal, weil die Karte
// stehen blieb: die Mutationen invalidierten nur, und `useNotifications` haelt
// mit `placeholderData: keepPreviousData` den alten Stand sichtbar, bis der
// (nicht garantiert schnelle) `/api/notifications`-Refetch zurueck ist —
// zwischen `onSettled` (Knopf wieder aktiv) und dem Refetch war die Karte
// erneut anklickbar. Fix wie bei `useDismissNotification` (5.115): die
// betroffene Karte OPTIMISTISCH aus dem Cache filtern; bei Server-Fehler
// zuruecksetzen. Die bestehende Sperre waehrend der Mutation (pendingId) bleibt.
//
// POL-4 K4 (2026-07-28): das Zuruecksetzen war STUMM. Bei einem Server-Fehler
// verschwand die Karte, kam Sekunden spaeter von selbst zurueck, und niemand
// erfuhr warum — Marvins «ich habe sechsmal geklickt» in der umgekehrten
// Variante (bei FB-D blieb die Karte stehen, hier kommt sie zurueck). Der
// Rollback sagt jetzt, dass er stattgefunden hat: eine Meldung mit dem
// Server-Grund. Alle fuenfzehn Karten-Aktionen haengen an dieser einen Stelle.
function optimisticNotificationRemoval<TInput>(
  qc: ReturnType<typeof useQueryClient>,
  predicate: (
    input: TInput,
  ) => (item: NotificationsResponse["data"]["items"][number]) => boolean,
): {
  onMutate: (input: TInput) => Promise<DismissContext>;
  onError: (
    err: unknown,
    input: TInput,
    ctx: DismissContext | undefined,
  ) => void;
} {
  return {
    onMutate: async (input) =>
      await snapshotAndPatchNotificationCache(qc, predicate(input)),
    onError: (err, _input, ctx) => {
      restoreNotificationCache(qc, ctx);
      toast.error(
        err instanceof Error && err.message.length > 0
          ? err.message
          : "Aktion nicht gespeichert — die Karte ist zurück.",
      );
    },
  };
}

// Fingerprint-Dismiss deckt zwei Karten-Typen ab (ein Endpoint, `kind`
// steuert). HOME-14: `lead_duplicate` ist raus — die Ablehnung gibt es noch,
// aber sie raeumt keine Karte mehr weg, sondern einen Hinweis am Projekt.
const FINGERPRINT_KIND_TO_NOTIFICATION_TYPE: Record<
  "customer_link" | "empty_keep",
  NotificationType
> = {
  customer_link: "project_customer_suggestion",
  empty_keep: "project_empty_question",
};

export function useDismissNotification() {
  const qc = useQueryClient();
  return useMutation<
    { ok: true; data: { dismissed: number } },
    Error,
    {
      type: NotificationType;
      sourceEntityId: string;
      snoozeDays?: number | null;
    },
    DismissContext
  >({
    mutationFn: async (input) => {
      const payload: Record<string, unknown> = {
        type: input.type,
        sourceEntityId: input.sourceEntityId,
      };
      if (input.snoozeDays !== undefined) {
        payload.snoozeDays = input.snoozeDays;
      }
      const res = await apiPost<{ ok: true; data: { dismissed: number } }>(
        "/api/notifications/dismiss",
        payload,
      );
      return res;
    },
    onMutate: async (input) =>
      await snapshotAndPatchNotificationCache(qc, (item) => {
        if (item.type !== input.type) return false;
        return matchesSourceEntity(item, input.sourceEntityId);
      }),
    onSuccess: (_data, input) => {
      toast.success(
        snoozeToastMessage({
          type: input.type,
          snoozeDays: input.snoozeDays,
          bundle: false,
        }),
      );
    },
    onError: (err, _input, ctx) => {
      restoreNotificationCache(qc, ctx);
      toast.error(
        err instanceof Error
          ? err.message
          : "Benachrichtigung konnte nicht ausgeblendet werden.",
      );
    },
    onSettled: () => invalidateAll(qc),
  });
}

export function useDismissNotificationBundle() {
  const qc = useQueryClient();
  return useMutation<
    { ok: true; data: { dismissed: number } },
    Error,
    { type: NotificationType; snoozeDays?: number | null },
    DismissContext
  >({
    mutationFn: async (input) => {
      const payload: Record<string, unknown> = {
        type: input.type,
        bundle: true,
      };
      if (input.snoozeDays !== undefined) {
        payload.snoozeDays = input.snoozeDays;
      }
      const res = await apiPost<{ ok: true; data: { dismissed: number } }>(
        "/api/notifications/dismiss",
        payload,
      );
      return res;
    },
    onMutate: async (input) =>
      await snapshotAndPatchNotificationCache(
        qc,
        (item) => item.type === input.type,
      ),
    onSuccess: (_data, input) => {
      toast.success(
        snoozeToastMessage({
          type: input.type,
          snoozeDays: input.snoozeDays,
          bundle: true,
        }),
      );
    },
    onError: (err, _input, ctx) => {
      restoreNotificationCache(qc, ctx);
      toast.error(
        err instanceof Error
          ? err.message
          : "Benachrichtigungen konnten nicht ausgeblendet werden.",
      );
    },
    onSettled: () => invalidateAll(qc),
  });
}

// Per-Item-Match uses the gleiche Source-Entity-Ableitung wie das Frontend
// (NotificationRow) und der Endpoint — `sourceEntityIdFromItemId` strippt den
// Type-Prefix aus `item.id`. So bleibt das Match in Sync mit dem Schluessel,
// den `NotificationDismissalsRepo.upsertMany` schreibt.
function matchesSourceEntity(
  item: NotificationsResponse["data"]["items"][number],
  sourceEntityId: string,
): boolean {
  return sourceEntityIdFromItemId(item.id) === sourceEntityId;
}

// project_ready_to_complete — Primaer-Aktion „Abschliessen" (v6.3).
// Triggert den Status-Wechsel `in_progress → completed`. Der Server-Guard
// in `ProjectService.changeStatus` prueft nochmal Role + Readiness — die UI
// darf sich nicht alleine auf den Filter verlassen.
export function useCompleteProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      projectId: string;
      reason?: string | null;
      /**
       * ABS-3: das gewaehlte Bonus-Jahr + der Satz dazu. Fehlt das Jahr, nimmt
       * der Server den Vorschlag (laufendes Jahr). Die Schranken (welche
       * Jahre, wann der Satz Pflicht ist) prueft der Server — das Fenster
       * spiegelt sie nur, damit kein Klick in einen 400 laeuft.
       */
      bonusYear?: number | null;
      bonusYearReason?: string | null;
    }) => {
      const res = await apiPost<{ ok: true; data: unknown }>(
        `/api/projects/status?id=${encodeURIComponent(input.projectId)}`,
        {
          to: "completed",
          reason: input.reason ?? null,
          bonusYear: input.bonusYear ?? null,
          bonusYearReason: input.bonusYearReason ?? null,
        },
      );
      return res;
    },
    // ABS-1: der Abschluss loescht BEIDE Abschluss-Karten desselben Projekts
    // — die Feststellung des OS («abschlussreif») und die Anmeldung des PL.
    // Sandro kann von jeder der beiden aus freigeben; bliebe die andere
    // stehen, saehe er nach dem Klick noch eine Karte fuer ein Projekt, das
    // schon zu ist.
    ...optimisticNotificationRemoval<{
      projectId: string;
      reason?: string | null;
      bonusYear?: number | null;
      bonusYearReason?: string | null;
    }>(
      qc,
      (input) => (item) =>
        (item.type === "project_ready_to_complete" ||
          item.type === "project_completion_requested") &&
        item.projectId === input.projectId,
    ),
    onSuccess: (_data, variables) => {
      invalidateAll(qc);
      void qc.invalidateQueries({ queryKey: queryKeys.projects.all() });
      void qc.invalidateQueries({
        queryKey: queryKeys.projects.detail(variables.projectId),
      });
      void qc.invalidateQueries({
        queryKey: queryKeys.projects.completion(variables.projectId),
      });
      // ABS-3: der Verlauf hat eine neue Zeile, die Verdienst-Seite ein
      // neues Projekt im gewaehlten Jahr.
      void qc.invalidateQueries({
        queryKey: queryKeys.projects.events(variables.projectId),
      });
      void qc.invalidateQueries({ queryKey: queryKeys.earnings.all() });
    },
  });
}

/**
 * ABS-3 «Bonus-Jahr ändern» — die Korrektur NACH der Freigabe. Zweiter
 * Einstieg in dieselbe Server-Regel wie das Jahr-Feld im Freigabe-Fenster
 * (`decideBonusYear`), dasselbe Verlaufs-Ereignis. Nur admin/superadmin.
 */
export function useChangeBonusYear() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      projectId: string;
      bonusYear: number;
      reason: string | null;
    }) => {
      return await apiPost<GenericOk>(
        `/api/projects/completion?action=bonus-year&projectId=${encodeURIComponent(input.projectId)}`,
        { bonusYear: input.bonusYear, reason: input.reason },
      );
    },
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({
        queryKey: queryKeys.projects.detail(variables.projectId),
      });
      void qc.invalidateQueries({
        queryKey: queryKeys.projects.completion(variables.projectId),
      });
      void qc.invalidateQueries({
        queryKey: queryKeys.projects.events(variables.projectId),
      });
      void qc.invalidateQueries({ queryKey: queryKeys.earnings.all() });
    },
  });
}

// ---------------------------------------------------------------------------
// ABS-1 — der Abschluss-Vorgang: anmelden, zurueckweisen, zur Kenntnis nehmen
// ---------------------------------------------------------------------------
// Die FREIGABE steht bewusst nicht hier — sie ist `useCompleteProject` oben,
// derselbe `POST /api/projects/status`-Weg wie bisher. Ein zweiter Schreibweg
// auf `projects.status` waere eine zweite Wahrheit ueber den Abschluss.

/** «Zum Abschluss anmelden» — der PL sagt, dass er fertig ist. */
export function useRequestProjectCompletion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { projectId: string; note: string | null }) => {
      return await apiPost<GenericOk>(
        `/api/projects/completion?action=request&projectId=${encodeURIComponent(input.projectId)}`,
        { note: input.note },
      );
    },
    // Die Rueckweisungs-Karte ist mit der Anmeldung erledigt: der Vorgang
    // liegt wieder bei Sandro.
    ...optimisticNotificationRemoval<{
      projectId: string;
      note: string | null;
    }>(
      qc,
      (input) => (item) =>
        item.type === "project_completion_rejected" &&
        item.projectId === input.projectId,
    ),
    onSuccess: (_data, variables) => {
      invalidateAll(qc);
      void qc.invalidateQueries({
        queryKey: queryKeys.projects.detail(variables.projectId),
      });
      void qc.invalidateQueries({
        queryKey: queryKeys.projects.completion(variables.projectId),
      });
    },
  });
}

/** «Zurueckweisen» — Sandros zweite Antwort, mit Begruendung. */
export function useRejectProjectCompletion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { projectId: string; reason: string }) => {
      return await apiPost<GenericOk>(
        `/api/projects/completion?action=reject&projectId=${encodeURIComponent(input.projectId)}`,
        { reason: input.reason },
      );
    },
    ...optimisticNotificationRemoval<{ projectId: string; reason: string }>(
      qc,
      (input) => (item) =>
        item.type === "project_completion_requested" &&
        item.projectId === input.projectId,
    ),
    onSuccess: (_data, variables) => {
      invalidateAll(qc);
      void qc.invalidateQueries({
        queryKey: queryKeys.projects.detail(variables.projectId),
      });
      void qc.invalidateQueries({
        queryKey: queryKeys.projects.completion(variables.projectId),
      });
    },
  });
}

/**
 * «Zur Kenntnis» — der PL schliesst die Rueckweisung ab.
 *
 * Das ist KEIN Wegklicken der Meldung, sondern eine echte Zustands-Aenderung
 * (`completion_rejected_at` faellt). Ein Event-Dismiss waere permanent je
 * (User, Type, Projekt) — eine zweite Rueckweisung desselben Projekts kaeme
 * danach nie mehr an.
 */
export function useAcknowledgeCompletionRejection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { projectId: string }) => {
      return await apiPost<GenericOk>(
        `/api/projects/completion?action=acknowledge&projectId=${encodeURIComponent(input.projectId)}`,
        {},
      );
    },
    ...optimisticNotificationRemoval<{ projectId: string }>(
      qc,
      (input) => (item) =>
        item.type === "project_completion_rejected" &&
        item.projectId === input.projectId,
    ),
    onSuccess: (_data, variables) => {
      invalidateAll(qc);
      void qc.invalidateQueries({
        queryKey: queryKeys.projects.detail(variables.projectId),
      });
      void qc.invalidateQueries({
        queryKey: queryKeys.projects.completion(variables.projectId),
      });
    },
  });
}
